import { useMemo, useState } from "react";
import { Info } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import {
  AgentDetailDialog,
  BuyAgentDialog,
  BuyNoticeDialog,
  C,
  DeliveryAddressDialog,
  IncomeHistoryDialog,
  TransferDialog,
} from "@/components/modal/home";
import { useFortAgentSkuConfig } from "@/hooks/api/useFortAgentSkuConfig";
import { FortAgentSkuConfigItem, MyFortAgentItem } from "@/api/types";
import { useNavigate } from "react-router";
import { useAppKit } from "@reown/appkit/react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/store/useUserStore";
import { useGetMyFortAgents } from "@/hooks/api/useGetMyFortAgents";
import { AgentMarketSection } from "@/components/home/AgentMarketSection";
import { HomeOfficialChannels } from "@/components/home/HomeOfficialChannels";
import { HomeMyAgentsSectionBody } from "@/components/home/MyAgentsSection";
import { useGetUserInfo } from "@/hooks/api/useGetUserInfo";

// ===== 主页面 =====
export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: openAppKit } = useAppKit();

  const { userInfo } = useUserStore();
  const [buyAgent, setBuyAgent] = useState<FortAgentSkuConfigItem | null>(null);
  const [detailAgent, setDetailAgent] = useState<any>(null);
  const [addressOpen, setAddressOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferAgent, setTransferAgent] = useState<MyFortAgentItem | null>(
    null
  );
  const [incomeHistoryOpen, setIncomeHistoryOpen] = useState(false);
  const [buyNoticeOpen, setBuyNoticeOpen] = useState(false);
  const {
    data: skuConfig,
    isLoading: skuLoading,
    error: skuError,
  } = useFortAgentSkuConfig();
  const {
    data: myAgents,
    isLoading: myAgentsLoading,
    error: myAgentsError,
  } = useGetMyFortAgents();
  // console.log("skuConfig", { skuLoading, skuError, skuConfig });
  // console.log("myAgents", { myAgentsLoading, myAgentsError, myAgents });
  const { marketSoldTotal, marketSupplyCap, soldPct } = useMemo(() => {
    const skus = skuConfig?.skus ?? [];
    let sold = 0;
    let cap = 0;
    for (const s of skus) {
      // sold += s.sellCount ?? 0;
      sold += +s.supplyCap - +s.leftSupplyCount;
      cap += s.supplyCap ?? 0;
    }
    const pct = cap > 0 ? (sold / cap) * 100 : 0;
    return {
      marketSoldTotal: sold,
      marketSupplyCap: cap,
      soldPct: pct,
    };
  }, [skuConfig?.skus]);

  return (
    <div className="pt-top-header-relaxed  px-4 space-y-6 max-w-lg mx-auto">
      {/* ===== Hero（与 dapp 一致：ScrollReveal） ===== */}
      {/* <ScrollReveal
        direction="up"
        distance={20}
        duration={0.5}
        className="space-y-4 relative z-10"
      > */}
      <div className="relative">
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-emerald-500/10 blur-[80px] rounded-full" />
        <div className="relative">
          <h1 className="text-3xl font-black tracking-tight leading-tight mb-1">
            {t("homePage.hero.title")}
            <span className="brand-gradient">
              {t("homePage.hero.titleHighlight")}
            </span>
            {t("homePage.hero.titleSuffix")}
          </h1>
          <p
            className="text-[12px] mt-1.5 flex items-center gap-1.5 leading-snug"
            style={{ color: "rgba(160,170,190,0.88)" }}
          >
            <span
              className="inline-block w-3 h-px shrink-0"
              style={{ background: "rgba(34,197,94,0.35)" }}
            />
            {t("homePage.hero.subtitle")}
          </p>
        </div>
      </div>

      {/* 统计面板 */}
      {/* <div className="grid grid-cols-2 gap-2">
          {[
            {
              label: "平台 TVL",
              value: `$${formatThousands(MOCK_STATS.totalTvl)}`,
              icon: <Shield size={11} />,
            },
            {
              label: "参与用户",
              value: `${(MOCK_STATS.totalUsers / 1000).toFixed(1)}K`,
              icon: <Users size={11} />,
            },
            {
              label: "累计交易量",
              value: `$${formatThousands(MOCK_STATS.totalTradeVolume)}`,
              icon: <TrendingUp size={11} />,
            },
            {
              label: `${getTokenName(CCC_AIF_ADDRESS)} 回购`,
              value: `$${formatThousands(MOCK_STATS.aifBuybackAmount)}`,
              icon: <Flame size={11} />,
            },
          ].map(s => (
            <div
              key={s.label}
              className="p-3 rounded-lg"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
            >
              <div
                className="flex items-center gap-1 mb-1"
                style={{ color: C.dim }}
              >
                {s.icon}
                <span className="text-[10px]">{s.label}</span>
              </div>
              <div className="text-lg font-black mono-num">{s.value}</div>
            </div>
          ))}
        </div> */}
      {/* </ScrollReveal> */}

      {/* ===== 我的智能体（与 dapp：ScrollReveal） ===== */}
      <ScrollReveal
        direction="up"
        distance={0}
        delay={0.05}
        duration={0.55}
        className="space-y-2 relative z-10"
        id="my-agents-section"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-0.5 h-4 rounded-full shrink-0"
              style={{
                background: "linear-gradient(180deg, #22c55e, #2196f6)",
              }}
            />
            <h2 className="font-bold text-lg leading-tight">
              {t("homePage.myAgents.title")}
            </h2>
            {userInfo?.address && (myAgents?.agents?.length ?? 0) > 0 && (
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-bold shrink-0"
                style={{ background: `${C.primary}15`, color: C.primary }}
              >
                {t("homePage.myAgents.ownedCount", {
                  count: myAgents?.agents?.length ?? 0,
                })}
              </span>
            )}
          </div>
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-semibold shrink-0 py-1 px-1.5 rounded-lg transition-all hover:opacity-90"
            style={{ color: C.primary }}
            onClick={() => {
              if (!userInfo) {
                openAppKit();
                return;
              }
              navigate("/my-agents");
            }}
          >
            <Info size={12} strokeWidth={2.25} />
            {t("homePage.myAgents.more")}
          </button>
        </div>

        <HomeMyAgentsSectionBody
          hasUser={!!userInfo?.address}
          myAgentsLoading={myAgentsLoading}
          agents={myAgents?.agents}
          onConnectWallet={() => openAppKit()}
          onGoBuyMarket={() =>
            document
              .getElementById("market")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          onAddress={() => setAddressOpen(true)}
          onGoManage={() => navigate("/my-agents")}
        />
      </ScrollReveal>

      {/* ===== 智能体市场（外层 + 内层统计/卡片见 AgentMarketSection） ===== */}
      <ScrollReveal
        direction="up"
        distance={0}
        delay={0.05}
        duration={0.55}
        className="relative z-10"
        id="market"
      >
        <AgentMarketSection
          variant="home"
          skuLoading={skuLoading}
          skus={skuConfig?.skus}
          marketSoldTotal={marketSoldTotal}
          marketSupplyCap={marketSupplyCap}
          soldPct={soldPct}
          onOpenBuyNotice={() => setBuyNoticeOpen(true)}
          onBuy={agent => {
            if (!userInfo) {
              openAppKit();
              return;
            }
            setBuyAgent(agent);
          }}
          onDetail={agent => setDetailAgent(agent)}
        />
      </ScrollReveal>

      <ScrollReveal direction="up" distance={0} delay={0.1} duration={0.45}>
        <HomeOfficialChannels />
      </ScrollReveal>

      {/* Dialogs */}
      <BuyAgentDialog
        agent={buyAgent}
        open={!!buyAgent}
        onClose={() => setBuyAgent(null)}
        canMix={userInfo?.canMix ?? false}
      />
      <AgentDetailDialog
        agent={detailAgent}
        open={!!detailAgent}
        onClose={() => setDetailAgent(null)}
      />
      <DeliveryAddressDialog
        open={addressOpen}
        onClose={() => setAddressOpen(false)}
      />
      {transferAgent != null && (
        <TransferDialog
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          agent={transferAgent}
        />
      )}
      <IncomeHistoryDialog
        open={incomeHistoryOpen}
        onClose={() => setIncomeHistoryOpen(false)}
      />
      <BuyNoticeDialog
        open={buyNoticeOpen}
        onClose={() => setBuyNoticeOpen(false)}
      />
    </div>
  );
}
